import { type TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { useTypedParams } from '@sorare/routing';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { ANY_SPORT_MY_CARDS_GALLERY_RARITY_SET_LEAGUE } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { range } from '@sorare/core/src/lib/arrays';
import { COMPETITION_COLORS, isHexColor } from '@sorare/core/src/lib/colors';
import { isType } from '@sorare/core/src/lib/gql';
import { isRarityStr } from '@sorare/core/src/lib/so5';
import {
  desktopAndAbove,
  laptopAndAbove,
  largeDesktopAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { CollectionSetSwitcher } from 'components/myCards/CollectionSetSwitcher';
import {
  CollectionSlotsPreview,
  Fallback as CollectionSlotsPreviewFallback,
} from 'components/myCards/LeagueCollectionPreview/CollectionSlotsPreview';
import { LeaguePageHeader } from 'components/myCards/LeaguePageHeader';
import { LeagueSearchInput } from 'components/myCards/LeagueSearchInput';
import {
  useGallerySearch,
  useGallerySearchResults,
} from 'hooks/my-cards/useGallerySearch';

import type {
  LeagueSlugPageQuery,
  LeagueSlugPageQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

const Root = styled.div`
  isolation: isolate;
  padding-bottom: var(--quadruple-unit);
`;

const Header = styled(Vertical)`
  position: relative;
  isolation: isolate;
`;

const TextureBackgroundWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  & > * {
    width: 100%;
    height: 100%;
  }
  &:after {
    background: linear-gradient(
      180deg,
      rgba(var(--c-rgb-black), 0) 50%,
      var(--c-black) 100%
    );
    content: '';
    position: absolute;
    inset: 0;
  }
`;

const CollectionsList = styled(Vertical)`
  padding: 0 var(--double-unit);
  @media ${laptopAndAbove} {
    display: grid;
    --nb-columns: 1;
    align-items: stretch;
    grid-template-columns: repeat(var(--nb-columns), minmax(0, 1fr));
  }
  @media ${desktopAndAbove} {
    --nb-columns: 2;
  }
  @media ${largeDesktopAndAbove} {
    --nb-columns: 3;
  }
`;

const StyledNeonEdge = styled(NeonEdge)`
  height: 100%;
`;

const SlotPreviewWrapper = styled.div`
  border: 1px solid var(--c-nd-50);
  border-radius: var(--unit);
  overflow: hidden;
`;

const InnerSlotPreviewWrapper = styled.div`
  padding: var(--double-unit);
`;

const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
  z-index: -1;
`;

const Divider = styled.div`
  height: 1px;
  background: var(--c-nd-150);
  margin: var(--double-unit);
`;

const CollectionSetSwitcherWrapper = styled.div`
  align-self: center;
`;

const LEAGUE_SLUG_PAGE_QUERY = gql`
  query LeagueSlugPageQuery(
    $leagueSlug: String!
    $setSlug: String!
    $userSlug: String!
    $sport: Sport!
  ) {
    currentUser {
      slug
      competitionCardCollections(
        competitionSlug: $leagueSlug
        cardSetSlug: $setSlug
        sport: $sport
      ) {
        cardCollections {
          slug
          anyTeam {
            slug
            name
            shortName
            code
            styleGuideColor
            ... on Club {
              slug
              domesticLeague {
                slug
                displayName
              }
            }
          }
          userCardCollection(forUserSlug: $userSlug) {
            id
            slots {
              slug
              slot {
                id
                anyPlayer {
                  slug
                  displayName
                }
              }
            }
          }
          ...CollectionSlotsPreview_cardCollection
          ...useGallerySearchResults_cardCollection
        }
        ...LeaguePageHeader_competitionCardCollections
      }
    }
  }
  ${CollectionSlotsPreview.fragments.cardCollection}
  ${useGallerySearchResults.fragments.cardCollection}
  ${LeaguePageHeader.fragments.competitionCardCollections}
` as TypedDocumentNode<LeagueSlugPageQuery, LeagueSlugPageQueryVariables>;

const LeagueSlugPageComponent = ({ rarity }: { rarity: Rarity }) => {
  const { currentUser } = useCurrentUserContext();
  const { generateSportContextPath, sport } = useSportContext();
  const { league: leagueSlug, set: setSlug } = useTypedParams<PageParams>();
  const { data, loading } = useQuery(LEAGUE_SLUG_PAGE_QUERY, {
    variables: {
      leagueSlug,
      setSlug,
      userSlug: currentUser?.slug || '',
      sport,
    },
  });

  const competitionCardCollections =
    data?.currentUser?.competitionCardCollections.at(0);
  const { cardCollections } = competitionCardCollections || {};
  const anyTeam = cardCollections?.at(0)?.anyTeam;
  const competition = isType(anyTeam, 'Club') ? anyTeam.domesticLeague : null;
  const { search, onSearchChange } = useGallerySearch();
  const results = useGallerySearchResults(search, cardCollections);
  return (
    <>
      <PageHeader text={competition?.displayName}></PageHeader>
      <TextureBackgroundWrapper>
        <TextureBackground
          background="wave2"
          mode="dark"
          color={COMPETITION_COLORS[leagueSlug] || 'var(--c-nd-150)'}
        />
      </TextureBackgroundWrapper>
      <Root>
        <Header>
          <LeaguePageHeader
            competitionCardCollections={competitionCardCollections}
            loading={loading}
          />
          <CollectionSetSwitcherWrapper>
            <CollectionSetSwitcher
              cardSetSlug={setSlug}
              path={cardSetSlug =>
                generateSportContextPath(
                  ANY_SPORT_MY_CARDS_GALLERY_RARITY_SET_LEAGUE,
                  {
                    params: {
                      rarity,
                      set: cardSetSlug,
                      league: leagueSlug,
                    },
                  }
                )
              }
            />
          </CollectionSetSwitcherWrapper>
          <LeagueSearchInput
            onChange={onSearchChange}
            value={search}
            collectionsCount={cardCollections?.length || 0}
          />
          <Divider />
        </Header>
        <CollectionsList gap={2}>
          {loading && !cardCollections?.length
            ? range(6).map(i => <CollectionSlotsPreviewFallback key={i} />)
            : results?.map(({ cardCollection, hitType }) => {
                const collectionColor = isHexColor(
                  cardCollection.anyTeam?.styleGuideColor
                )
                  ? cardCollection.anyTeam?.styleGuideColor
                  : 'var(--c-nd-150)';
                return (
                  <SlotPreviewWrapper key={cardCollection.slug}>
                    <StyledNeonEdge
                      color={collectionColor}
                      side="top"
                      spread="40%"
                      showHalo="always"
                    >
                      <StyledTextureBackground
                        background="wave2"
                        mode="dark"
                        color={collectionColor}
                      />
                      <InnerSlotPreviewWrapper>
                        <CollectionSlotsPreview
                          key={cardCollection.slug}
                          cardCollection={cardCollection}
                          playerNameFilter={
                            hitType === 'player' ? search.trim() : undefined
                          }
                        />
                      </InnerSlotPreviewWrapper>
                    </StyledNeonEdge>
                  </SlotPreviewWrapper>
                );
              })}
        </CollectionsList>
      </Root>
    </>
  );
};

const LeagueSlugPage = () => {
  const { rarity } = useTypedParams<PageParams>();

  if (!isRarityStr(rarity)) {
    return null;
  }
  return <LeagueSlugPageComponent rarity={rarity} />;
};
export default LeagueSlugPage;
